<template>
  <main
    id="quote"
    class="page-container"
    v-if="$route.name === 'operator-quotes'"
  >
    <div class="table-container-quote">
      <div class="tbl-wrap">
        <table class="tbl quote-table">
          <thead>
            <tr>
              <th @click="setSort('clientName')" class="sortable-th">
                Nome cliente
                <span class="sort-indicator">{{
                  sortIndicator("clientName")
                }}</span>
              </th>
              <th>Nome evento</th>
              <th @click="setSort('rentStartDate')" class="sortable-th">
                Data
                <span class="sort-indicator">{{
                  sortIndicator("rentStartDate")
                }}</span>
              </th>
              <th id="luogo">Luogo</th>
              <th id="apri-file">Lista materiali</th>
              <th id="stato">Stato</th>
              <th>Modifica materiali/zone</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(quote, index) in paginatedQuotes" :key="index">
              <tr class="quote-row">
                <td>{{ quote.Client.clientName }}</td>
                <td>{{ quote.eventName }}</td>
                <td>
                  <em>inizio</em> {{ formatToLocalDate(quote.rentStartDate) }} -
                  <em>fine</em> {{ formatToLocalDate(quote.rentEndDate) }} ({{
                    quote.daysCount
                  }})
                </td>
                <td>
                  <a
                    id="link-luogo"
                    v-if="quote.location && quote.location.link"
                    :href="quote.location.link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ quote.location.address || "Indirizzo non disponibile" }}
                  </a>
                  <span v-else>Indirizzo non disponibile</span>
                </td>
                <td>
                  <span
                    id="open-file"
                    @click="openFile(quote)"
                    title="Genera file PDF lista materiali"
                  >
                    <font-awesome-icon :icon="['fas', 'file-export']" />
                    Genera file
                  </span>
                </td>
                <td>
                  <div class="status-badge" :class="stateClass(quote.state)">
                    {{ quote.state || "—" }}
                  </div>
                </td>
                <td>
                  <div class="action-wrapper">
                    <span
                      v-if="quote.state === 'Accettato'"
                      class="edit-materials-btn"
                      @click="openEditModal(quote)"
                      role="button"
                      tabindex="0"
                      aria-label="Modifica lista materiali/zone"
                      @keydown.enter="openEditModal(quote)"
                      @keydown.space.prevent="openEditModal(quote)"
                    >
                      <font-awesome-icon :icon="['fas', 'pen-to-square']" />
                      Modifica
                    </span>
                    <span
                      v-else
                      class="edit-materials-disabled"
                      :title="
                        quote.state === 'Inviato'
                          ? 'Il preventivo deve essere Accettato per modificare i materiali/zone'
                          : 'Non è possibile modificare materiali/zone in questo stato'
                      "
                    >
                      —
                    </span>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <div v-if="sortedQuotes.length === 0" class="no-items">
          <p>Nessun preventivo disponibile</p>
        </div>
      </div>

      <!-- PAGINAZIONE -->
      <div v-if="showPagination" class="pagination-container">
        <div class="pagination-info">
          Mostrando {{ startItem }}-{{ endItem }} di
          {{ sortedQuotes.length }} preventivi
        </div>

        <div class="pagination-controls">
          <button
            @click="prevPage"
            :disabled="currentPage === 1"
            class="pagination-btn"
          >
            ‹ Precedente
          </button>

          <div class="page-numbers">
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="goToPage(page)"
              :class="{ 'page-btn': true, active: page === currentPage }"
            >
              {{ page }}
            </button>

            <span v-if="showEllipsis" class="ellipsis">...</span>
          </div>

          <button
            @click="nextPage"
            :disabled="currentPage === totalPages"
            class="pagination-btn"
          >
            Successiva ›
          </button>
        </div>

        <div class="pagination-size">
          <label for="pageSize">Preventivi per pagina:</label>
          <select
            id="pageSize"
            v-model="pageSize"
            @change="onPageSizeChange"
            class="page-size-select"
          >
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
      </div>
    </div>

    <!-- MODALE MODIFICA MATERIALI -->
    <Teleport to="body">
      <div
        v-if="editModal.open"
        class="modal-overlay"
        @click.self="closeEditModal"
        @keydown.escape="closeEditModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-modal-title"
      >
        <div class="modal edit-materials-modal" ref="editModalEl">
          <button
            class="modal__close"
            @click="closeEditModal"
            aria-label="Chiudi modale"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>

          <h2 id="edit-modal-title" class="modal__title">
            Modifica lista materiali/zone
          </h2>
          <p class="modal__subtitle">
            {{ editModal.quote?.Client?.clientName }} —
            {{ editModal.quote?.eventName }}
          </p>

          <!-- TAB BAR -->
          <div class="edit-modal-tabs" role="tablist">
            <button
              role="tab"
              :aria-selected="editModal.activeTab === 'add'"
              :class="[
                'edit-modal-tab',
                { active: editModal.activeTab === 'add' },
              ]"
              @click="editModal.activeTab = 'add'"
            >
              <font-awesome-icon :icon="['fas', 'circle-plus']" />
              Aggiungi articolo minore
            </button>
            <button
              role="tab"
              :aria-selected="editModal.activeTab === 'area'"
              :class="[
                'edit-modal-tab',
                { active: editModal.activeTab === 'area' },
              ]"
              @click="editModal.activeTab = 'area'"
            >
              <font-awesome-icon :icon="['fas', 'location-dot']" />
              Aggiorna zone
            </button>
          </div>

          <!-- TAB: AGGIUNGI ARTICOLO MINORE -->
          <div
            v-if="editModal.activeTab === 'add'"
            role="tabpanel"
            class="edit-modal-panel"
          >
            <div class="form-field">
              <label for="item-search" class="form-label"
                >Articolo minore *</label
              >
              <input
                id="item-search"
                v-model="addForm.search"
                type="text"
                class="form-input"
                placeholder="Cerca per nome…"
                autocomplete="off"
                @input="addForm.selectedItem = null"
              />
              <ul
                v-if="filteredItems.length && !addForm.selectedItem"
                class="item-suggestions"
                role="listbox"
                aria-label="Articoli disponibili"
              >
                <li
                  v-for="item in filteredItems"
                  :key="item.id"
                  class="item-suggestion"
                  role="option"
                  @click="selectItem(item)"
                >
                  <span class="item-suggestion-name">{{ item.itemName }}</span>
                  <span class="item-suggestion-cat">{{ item.category }}</span>
                </li>
              </ul>
              <div v-if="addForm.selectedItem" class="item-selected-badge">
                <font-awesome-icon :icon="['fas', 'circle-check']" />
                {{ addForm.selectedItem.itemName }}
                <button
                  type="button"
                  class="item-selected-clear"
                  @click="
                    addForm.selectedItem = null;
                    addForm.search = '';
                  "
                  aria-label="Rimuovi selezione"
                >
                  <font-awesome-icon :icon="['fas', 'xmark']" />
                </button>
              </div>
            </div>

            <div class="form-row">
              <div class="form-field">
                <label for="add-quantity" class="form-label">Quantità *</label>
                <input
                  id="add-quantity"
                  v-model.number="addForm.quantity"
                  type="number"
                  min="1"
                  class="form-input"
                  placeholder="es. 2"
                />
              </div>
              <div class="form-field">
                <label for="add-area" class="form-label">Zona</label>
                <input
                  id="add-area"
                  v-model="addForm.area"
                  type="text"
                  class="form-input"
                  placeholder="es. Sala A"
                />
              </div>
            </div>

            <div v-if="addForm.warnings.length" class="edit-modal-warnings">
              <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
              <ul>
                <li v-for="(w, i) in addForm.warnings" :key="i">{{ w }}</li>
              </ul>
            </div>

            <div class="modal-actions">
              <button
                type="button"
                class="btn-secondary"
                @click="closeEditModal"
              >
                Annulla
              </button>
              <button
                type="button"
                class="btn-primary"
                :disabled="
                  !addForm.selectedItem || !addForm.quantity || addForm.loading
                "
                @click="submitAddSubItem"
              >
                <font-awesome-icon
                  v-if="addForm.loading"
                  :icon="['fas', 'spinner']"
                  spin
                />
                <template v-else>Aggiungi articolo minore</template>
              </button>
            </div>
          </div>

          <!-- TAB: AGGIORNA ZONE -->
          <div
            v-if="editModal.activeTab === 'area'"
            role="tabpanel"
            class="edit-modal-panel"
          >
            <p v-if="!editModal.quote?.items?.length" class="no-items">
              Nessun articolo presente nel preventivo.
            </p>
            <div v-else class="area-items-list">
              <div
                v-for="item in areaForm.items"
                :key="item.quoteItemId"
                class="area-item-row"
              >
                <div class="area-item-info">
                  <span class="area-item-name">{{ item.itemName }}</span>
                  <span class="area-item-qty">Q.tà: {{ item.quantity }}</span>
                </div>
                <div class="area-item-input">
                  <label
                    :for="'area-' + item.quoteItemId"
                    class="form-label-sr"
                  >
                    Zona per {{ item.itemName }}
                  </label>
                  <input
                    :id="'area-' + item.quoteItemId"
                    v-model="item.area"
                    type="text"
                    class="form-input"
                    placeholder="es. Sala A"
                  />
                </div>
              </div>
            </div>

            <div class="modal-actions">
              <button
                type="button"
                class="btn-secondary"
                @click="closeEditModal"
              >
                Annulla
              </button>
              <button
                type="button"
                class="btn-primary"
                :disabled="!editModal.quote?.items?.length || areaForm.loading"
                @click="submitUpdateArea"
              >
                <font-awesome-icon
                  v-if="areaForm.loading"
                  :icon="['fas', 'spinner']"
                  spin
                />
                <template v-else>Salva zone</template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </main>
</template>

<script>
import logoPath from "@/assets/4AllEvent_logo.png";
import jsPDF from "jspdf";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import { formatToLocalDate } from "../utilities/formatting";
import { useToast } from "vue-toast-notification";

export default {
  setup() {
    const toast = useToast();
    return { toast };
  },
  computed: {
    ...mapGetters({
      allQuotes: "quotes/list",
      allItems: "items/list",
    }),

    sortedQuotes() {
      if (!this.sortKey) return this.allQuotes;
      return [...this.allQuotes].sort((a, b) => {
        let aVal, bVal;
        if (this.sortKey === "clientName") {
          aVal = a.Client?.clientName?.toLowerCase() || "";
          bVal = b.Client?.clientName?.toLowerCase() || "";
        } else {
          aVal = a[this.sortKey] ?? "";
          bVal = b[this.sortKey] ?? "";
        }
        if (aVal < bVal) return this.sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return this.sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    },

    totalPages() {
      return Math.ceil(this.sortedQuotes.length / this.pageSize);
    },

    paginatedQuotes() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.sortedQuotes.slice(start, start + this.pageSize);
    },

    showPagination() {
      return this.sortedQuotes.length > 0;
    },

    startItem() {
      return (this.currentPage - 1) * this.pageSize + 1;
    },

    endItem() {
      const end = this.currentPage * this.pageSize;
      return end > this.sortedQuotes.length ? this.sortedQuotes.length : end;
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      start = Math.max(1, end - maxVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },

    showEllipsis() {
      return this.totalPages > this.visiblePages[this.visiblePages.length - 1];
    },

    filteredItems() {
      const q = this.addForm.search.trim().toLowerCase();
      if (!q || this.addForm.selectedItem) return [];
      return this.allItems
        .filter((i) => i.itemName.toLowerCase().includes(q))
        .slice(0, 10);
    },
  },
  data() {
    return {
      image: logoPath,
      currentPage: 1,
      pageSize: 25,
      sortKey: "",
      sortOrder: "asc",
      editModal: {
        open: false,
        quote: null,
        activeTab: "add",
      },
      addForm: {
        search: "",
        selectedItem: null,
        quantity: 1,
        area: "",
        loading: false,
        warnings: [],
      },
      areaForm: {
        items: [],
        loading: false,
      },
    };
  },
  watch: {
    allQuotes() {
      this.currentPage = 1;
    },
  },
  methods: {
    ...mapActions([
      "fetchOperatorQuotes",
      "fetchItems",
      "uploadQuoteDocument",
      "addSubItemByOperator",
      "updateAreaByOperator",
    ]),

    formatToLocalDate,

    setSort(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
      this.currentPage = 1;
    },

    sortIndicator(key) {
      if (this.sortKey !== key) return "⇅";
      return this.sortOrder === "asc" ? "▲" : "▼";
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.scrollToTop();
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.scrollToTop();
      }
    },

    goToPage(page) {
      this.currentPage = page;
      this.scrollToTop();
    },

    onPageSizeChange() {
      this.currentPage = 1;
    },

    stateClass(state) {
      if (!state) return "";
      const s = state.trim();
      if (s === "Inviato") return "status-sent";
      if (s === "Accettato") return "status-accepted";
      if (s === "In consegna") return "status-delivery";
      if (s === "Chiuso") return "status-closed";
      return "";
    },

    scrollToTop() {
      const tableContainer = document.querySelector(".tbl-wrap");
      if (tableContainer) {
        tableContainer.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },

    async getQuotes() {
      try {
        await this.fetchOperatorQuotes();
      } catch (error) {
        this.toast.error(
          error.response?.data?.details ||
            "Errore durante la visualizzazione dei preventivi."
        );
      }
    },

    // ── EDIT MODAL ──────────────────────────────────────────────

    openEditModal(quote) {
      this.editModal.quote = quote;
      this.editModal.activeTab = "add";
      this.editModal.open = true;

      // Inizializza areaForm con gli articoli del preventivo
      this.areaForm.items = (quote.items || []).map((item) => ({
        quoteItemId: item.QuoteItem.id,
        itemName: item.itemName,
        quantity: item.QuoteItem.quantity,
        area: item.QuoteItem.area || "",
      }));

      // Reset addForm
      this.addForm.search = "";
      this.addForm.selectedItem = null;
      this.addForm.quantity = 1;
      this.addForm.area = "";
      this.addForm.warnings = [];

      this.$nextTick(() => {
        this.$refs.editModalEl?.focus();
      });
    },

    closeEditModal() {
      this.editModal.open = false;
      this.editModal.quote = null;
    },

    selectItem(item) {
      this.addForm.selectedItem = item;
      this.addForm.search = item.itemName;
    },

    async submitAddSubItem() {
      if (!this.addForm.selectedItem || !this.addForm.quantity) return;
      this.addForm.loading = true;
      this.addForm.warnings = [];
      try {
        const res = await this.addSubItemByOperator({
          quoteId: this.editModal.quote.id,
          itemId: this.addForm.selectedItem.id,
          quantity: this.addForm.quantity,
          area: this.addForm.area || undefined,
        });
        if (res.data?.warnings?.length) {
          this.addForm.warnings = res.data.warnings;
        }
        this.toast.success("Articolo minore aggiunto con successo!");
        // Reset form
        this.addForm.search = "";
        this.addForm.selectedItem = null;
        this.addForm.quantity = 1;
        this.addForm.area = "";
        await this.getQuotes();
        this.closeEditModal();
        this.toast.info(
          "Genera nuovamente la lista materiali per vederla aggiornata."
        );
      } catch (error) {
        this.toast.error(
          error.response?.data?.details ||
            error.response?.data?.error ||
            "Errore durante l'aggiunta dell'articolo."
        );
      } finally {
        this.addForm.loading = false;
      }
    },

    async submitUpdateArea() {
      if (!this.areaForm.items.length) return;
      this.areaForm.loading = true;
      try {
        const updates = this.areaForm.items.map((i) => ({
          id: i.quoteItemId,
          area: i.area,
        }));
        await this.updateAreaByOperator({
          quoteId: this.editModal.quote.id,
          updates,
        });
        this.toast.success("Zone aggiornate con successo!");
        await this.getQuotes();
        this.closeEditModal();
        this.toast.info(
          "Genera nuovamente la lista materiali per vederla aggiornata."
        );
      } catch (error) {
        this.toast.error(
          error.response?.data?.details ||
            error.response?.data?.error ||
            "Errore durante l'aggiornamento delle zone."
        );
      } finally {
        this.areaForm.loading = false;
      }
    },

    // funzione per aprire il file "lista materiali" in formato PDF
    async openFile(quote) {
      const doc = new jsPDF();

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const x = 10;
      const y = 10;

      if (this.image) {
        try {
          doc.addImage(this.image, "PNG", x, y, 30, 40);
        } catch (error) {
          console.error("Errore durante l'aggiunta del logo:", error);
        }
      }

      doc.setFont("helvetica", "normal").setFontSize(9);
      const topRightX = pageWidth - 70;
      let topRightY = y + 5;
      [
        "Viale della libertà 21, 95040 Ramacca ( CT )",
        "P.IVA: 05459100870",
        "SDI: M5UXCR1",
        "amministrazione@4allevent.it",
        "(+39) 3207928979",
      ].forEach((text) => {
        doc.text(text, topRightX, topRightY);
        topRightY += 4;
      });

      const printLabelValue = (label, value, xPos, yPos, maxWidth = null) => {
        doc.setFont("helvetica", "bolditalic").setFontSize(11);
        doc.text(`${label}:`, xPos, yPos);
        const offset = doc.getTextWidth(`${label}: `) + 0.8;
        doc.setFont("helvetica", "normal");
        if (maxWidth) {
          const wrapped = doc.splitTextToSize(
            value || "N/D",
            maxWidth - offset
          );
          wrapped.forEach((ln, idx) =>
            doc.text(ln, xPos + offset, yPos + idx * 5)
          );
          return yPos + wrapped.length * 5;
        } else {
          doc.text(value || "N/D", xPos + offset, yPos);
          return yPos + 5;
        }
      };

      let contentY = y + 55;

      const loc =
        (typeof quote.location === "string"
          ? (() => {
              try {
                return JSON.parse(quote.location);
              } catch {
                return { address: quote.location };
              }
            })()
          : quote.location) || {};

      contentY = printLabelValue(
        "Data",
        `inizio ${this.formatToLocalDate(
          quote.rentStartDate
        )} - fine ${this.formatToLocalDate(quote.rentEndDate)} (${
          quote.daysCount
        })`,
        x,
        contentY
      );

      contentY = printLabelValue(
        "Cliente",
        quote.Client?.clientName || "N/D",
        x,
        contentY
      );
      contentY = printLabelValue("Luogo", loc.address || "N/D", x, contentY);

      if (loc.link) {
        const label = `Maps`;
        const cleanLink = loc.link.replace(/^https?:\/\//, "");
        const maxChars = 19;
        const display =
          cleanLink.length > maxChars
            ? cleanLink.slice(0, maxChars) + "…"
            : cleanLink;

        doc.setFont("helvetica", "bolditalic").setFontSize(11);
        doc.text(`${label}:`, x, contentY);
        const offset = doc.getTextWidth(`${label}: `) + 0.8;
        doc
          .setFont("helvetica", "bold")
          .setFontSize(10)
          .setTextColor(0, 0, 255);
        doc.textWithLink(display, x + offset, contentY, { url: loc.link });
        doc.setTextColor(0).setFont("helvetica", "normal").setFontSize(11);
        contentY += 6;
      }

      doc.setDrawColor(150, 150, 150);
      doc.setLineWidth(0.5);
      doc.line(10, contentY + 3, pageWidth - 10, contentY + 3);
      contentY += 8;
      contentY += 4;

      const sectionHeight = 10;
      const sectionWidth = pageWidth * 0.3 - 20;

      doc.setFillColor(220, 220, 220);
      doc.rect(x, contentY, sectionWidth, sectionHeight, "F");

      doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(0, 0, 0);
      doc.text("Lista materiali", x + 4, contentY + 7);

      contentY += sectionHeight + 8;

      const categoryBadgeMap = {
        "fari a batteria": { abbr: "BAT", r: 255, g: 152, b: 0 },
        "illuminotecnica scenica rgb": { abbr: "LUX", r: 255, g: 193, b: 7 },
        "controllo luci": { abbr: "CTL", r: 30, g: 136, b: 229 },
        audio: { abbr: "AUD", r: 67, g: 160, b: 71 },
        "mixer & mic": { abbr: "MIC", r: 0, g: 131, b: 143 },
        video: { abbr: "VID", r: 229, g: 57, b: 53 },
        "video control": { abbr: "VCT", r: 156, g: 39, b: 176 },
        "notebook-mac": { abbr: "MAC", r: 33, g: 150, b: 243 },
        "stage & truss": { abbr: "STG", r: 104, g: 159, b: 56 },
        "distribuzione elettrica": { abbr: "ELE", r: 255, g: 112, b: 67 },
        veicoli: { abbr: "VCL", r: 121, g: 85, b: 72 },
      };
      const BADGE_W = 13;
      const BADGE_H = 4;

      // Raggruppamento articoli per zona
      const groupedByArea = {};
      (quote.items || []).forEach((item) => {
        const areaName = item.QuoteItem?.area?.trim() || "Senza zona";
        if (!groupedByArea[areaName]) groupedByArea[areaName] = [];
        groupedByArea[areaName].push(item);
      });

      const sortedAreas = Object.keys(groupedByArea).sort((a, b) => {
        if (a === "Senza zona") return 1;
        if (b === "Senza zona") return -1;
        return a.localeCompare(b);
      });

      let globalIdx = 0;

      sortedAreas.forEach((areaName) => {
        // Controllo spazio per il titolo della zona
        if (contentY > pageHeight - 25) {
          doc.addPage();
          contentY = 20;
        }

        // Disegna intestazione zona
        doc
          .setFont("helvetica", "bold")
          .setFontSize(10)
          .setTextColor(60, 60, 60);
        doc.text(`ZONA: ${areaName.toUpperCase()}`, x, contentY);
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(x, contentY + 1.5, x + 60, contentY + 1.5);
        contentY += 10;

        groupedByArea[areaName].forEach((item) => {
          const { quantity } = item.QuoteItem;
          const checkboxSize = 4;
          const textX = x + checkboxSize + 2;
          const catKey = (item.category || "").toLowerCase().trim();
          const badge = categoryBadgeMap[catKey];
          const badgeOffset = badge ? BADGE_W + 3 : 0;
          const maxWidth = pageWidth - x - checkboxSize - 2 - 10 - badgeOffset;

          globalIdx++;

          doc.rect(x, contentY - checkboxSize, checkboxSize, checkboxSize);

          if (badge) {
            doc.setFillColor(badge.r, badge.g, badge.b);
            doc.rect(textX, contentY - 3.2, BADGE_W, BADGE_H, "F");
            doc
              .setFont("helvetica", "bold")
              .setFontSize(6)
              .setTextColor(255, 255, 255);
            doc.text(badge.abbr, textX + BADGE_W / 2, contentY - 0.5, {
              align: "center",
            });
          }

          doc.setFont("helvetica", "normal").setFontSize(11).setTextColor(0);
          const nameText = `${globalIdx}) ${item.itemName}`;
          const wrappedName = doc.splitTextToSize(nameText, maxWidth);
          doc.text(wrappedName, textX + badgeOffset, contentY);
          const nameBlockHeight = wrappedName.length * 5;

          const detailY = contentY + nameBlockHeight + 1;
          const qtyLabel = "Q.tà: ";
          const qtyValue = String(quantity);

          doc.setFontSize(9).setTextColor(80, 80, 80);
          doc.text(qtyLabel, textX, detailY);
          const qtyLabelW = doc.getTextWidth(qtyLabel);

          doc.setTextColor(30, 110, 200).setFont("helvetica", "bold");
          doc.text(qtyValue, textX + qtyLabelW, detailY);
          doc.setFont("helvetica", "normal").setTextColor(0);

          contentY += nameBlockHeight + 8;

          if (contentY > pageHeight - 20) {
            doc.addPage();
            contentY = 10;
          }
        });

        contentY += 4; // Spazio extra tra le zone
      });

      const usedCategories = [
        ...new Set(
          quote.items
            .map((item) => (item.category || "").toLowerCase().trim())
            .filter((k) => categoryBadgeMap[k])
        ),
      ];

      if (usedCategories.length > 0) {
        const LEGEND_BADGE_W = 13;
        const LEGEND_BADGE_H = 4;
        const LEGEND_COL_W = 52;
        const LEGEND_COLS = 3;
        const LEGEND_ROW_H = 8;
        const legendRows = Math.ceil(usedCategories.length / LEGEND_COLS);
        const legendBlockH = 10 + legendRows * LEGEND_ROW_H + 4;

        if (contentY + legendBlockH > pageHeight - 10) {
          doc.addPage();
          contentY = 10;
        }

        contentY += 6;
        doc.setDrawColor(150);
        doc.setLineWidth(0.3);
        doc.line(x, contentY, pageWidth - x, contentY);
        contentY += 5;

        doc
          .setFont("helvetica", "bold")
          .setFontSize(9)
          .setTextColor(80, 80, 80);
        doc.text("Legenda categorie", x, contentY);
        contentY += 6;

        usedCategories.forEach((key, i) => {
          const badge = categoryBadgeMap[key];
          const col = i % LEGEND_COLS;
          const row = Math.floor(i / LEGEND_COLS);
          const lx = x + col * LEGEND_COL_W;
          const ly = contentY + row * LEGEND_ROW_H;

          doc.setFillColor(badge.r, badge.g, badge.b);
          doc.rect(lx, ly - 3, LEGEND_BADGE_W, LEGEND_BADGE_H, "F");
          doc
            .setFont("helvetica", "bold")
            .setFontSize(6)
            .setTextColor(255, 255, 255);
          doc.text(badge.abbr, lx + LEGEND_BADGE_W / 2, ly - 0.2, {
            align: "center",
          });

          doc
            .setFont("helvetica", "normal")
            .setFontSize(8)
            .setTextColor(60, 60, 60);
          doc.text(key, lx + LEGEND_BADGE_W + 2, ly);
        });
      }

      const quoteId = quote.id;
      const filenameBase = `lista_materiali_N_${quoteId}.pdf`;
      const isDev =
        process.env.NODE_ENV === "development" ||
        process.env?.VUE_APP_NODE_ENV === "development";

      const withSuffixBeforeExt = (name, suffix) => {
        if (!suffix) return name;
        const dot = name.lastIndexOf(".");
        if (dot <= 0) return `${name}.${suffix}`;
        return `${name.slice(0, dot)}.${suffix}${name.slice(dot)}`;
      };

      const finalName = withSuffixBeforeExt(filenameBase, isDev ? "dev" : "");

      const blob = doc.output("blob");
      const file = new File([blob], finalName, { type: "application/pdf" });

      try {
        await this.$store.dispatch("uploadQuoteDocument", {
          quoteId,
          document: file,
          filename: finalName,
        });
        doc.save(finalName);
        this.toast.success(
          "PDF lista materiali generato e caricato con successo!"
        );
      } catch (error) {
        console.error("Errore durante l'upload del PDF:", error);
        this.toast.error(
          error.response?.data?.details ||
            error.response?.data?.error ||
            "Errore durante il caricamento del PDF."
        );
      }
    },
  },
  mounted() {
    this.getQuotes();
    this.fetchItems();
  },
};
</script>

<style src="@/css/QuotePage.css"></style>

<style scoped>
/* ── Colonna Modifica materiali ── */
.edit-materials-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: #2563eb;
  font-size: 14px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.15s, color 0.15s;
}

.edit-materials-btn:hover {
  background: #eff6ff;
  color: #1d4ed8;
}

.edit-materials-disabled {
  color: #bbb;
  font-size: 14px;
  cursor: help;
}

/* ── Badge di stato ── */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #f3f4f6;
  color: #6b7280;
  white-space: nowrap;
}

.status-sent {
  background: #e0f2fe;
  color: #0369a1;
}

.status-accepted {
  background: #dcfce7;
  color: #15803d;
}

.status-delivery {
  background: #fef9c3;
  color: #a16207;
}

.status-closed {
  background: #f3f4f6;
  color: #6b7280;
}

.action-wrapper {
  display: flex;
  align-items: center;
}

/* ── Modale modifica materiali ── */
.edit-materials-modal {
  max-width: 560px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  outline: none;
}

/* Tab bar */
.edit-modal-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 2px solid #eee;
  padding-bottom: 0;
}

.edit-modal-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
  border-radius: 4px 4px 0 0;
}

.edit-modal-tab:hover {
  color: #333;
  background: #f8f8f8;
}

.edit-modal-tab.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
}

/* Panel */
.edit-modal-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Form fields */
.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.form-label-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.form-input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  width: 100%;
  box-sizing: border-box;
}

.form-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

/* Autocomplete suggestions */
.item-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 50;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.item-suggestion {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  gap: 8px;
  transition: background 0.1s;
}

.item-suggestion:hover {
  background: #f0f7ff;
}

.item-suggestion-name {
  color: #111827;
  font-weight: 500;
}

.item-suggestion-cat {
  color: #9ca3af;
  font-size: 12px;
  white-space: nowrap;
}

/* Selected item badge */
.item-selected-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 4px;
  font-size: 13px;
  color: #166534;
  font-weight: 500;
}

.item-selected-clear {
  background: none;
  border: none;
  padding: 0 0 0 4px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  font-size: 12px;
}

.item-selected-clear:hover {
  color: #374151;
}

/* Warnings */
.edit-modal-warnings {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 4px;
  font-size: 13px;
  color: #92400e;
}

.edit-modal-warnings ul {
  margin: 0;
  padding-left: 16px;
}

/* Area items list */
.area-items-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
}

.area-item-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.area-item-info {
  flex: 1;
  min-width: 0;
}

.area-item-name {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.area-item-qty {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.area-item-input {
  width: 160px;
  flex-shrink: 0;
}

.area-item-input .form-input {
  font-size: 13px;
  padding: 6px 10px;
}

/* Action buttons */
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: #9ec44a;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-primary:hover:not(:disabled) {
  background: #85a83c;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

@media (max-width: 540px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .area-item-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .area-item-input {
    width: 100%;
  }
}
</style>
